/**
 * The routes: one bow per city pair, drawn as a hairline while the reader hovers that city.
 *
 * A route is a *route*, not a flight: several flights a day share one city pair and are drawn once,
 * because identical translucent strokes would compound into a brighter line than a single one. Its
 * lane — the bow it is drawn with — is stamped per route here, at payload ingest, rather than per
 * leg, so a line never jumps sideways on the tick another flight lands.
 *
 * Every drawn point comes from `@noko-city/shared`'s leg geometry, the same arithmetic the
 * dashboard's own map uses, so the aircraft `atlas-planes.ts` places and the line under it cannot
 * drift apart.
 */

import type * as MaplibreGl from "maplibre-gl";

import { distanceKm, EARTH_RADIUS_KM, type GeoPoint, legPath, legPhaseAt } from "@noko-city/shared";

/** The hovered city's routes: hairlines on a line layer of their own, under the glyphs. */
export const LEG_SOURCE = "noko-flight-legs";
export const LEG_LAYER = "noko-flight-legs";

/**
 * The hovered city's routes, at the decorative orbit planes' own weight — `.atlas-ring` is a 1 px
 * border (home.css) — and faint enough to read as a route instead of as a drawn line.
 */
export const LEG_WIDTH = 1;
export const LEG_HOVER_OPACITY = 0.45;

/**
 * A route's bow, as a fraction of the angular span it covers, per lane level.
 *
 * A few percent is a drawn flight path; a lane level of tens of percent would be a loop that
 * leaves the map, which is why the level is capped rather than proportional all the way out.
 * `LANE_FRACTION` is the one knob for "how curved is the curve" — the bow's profile is a sine and
 * is not meant to grow a second shape.
 */
const LANE_FRACTION = 0.05;
/**
 * The widest fan: lanes at 5 %, 10 % and 15 % of a route's own span, either side. Five
 * near-parallel routes resolve; a wider fan starts to look like noise rather than a bundle.
 */
const LANE_LEVEL_MAX = 2;
/**
 * The vertices a drawn route is sampled at. `legPath`'s own default of 64 is more than a bow of a
 * few percent needs to stay smooth at the closest chapter's zoom, and every vertex is a point the
 * renderer transforms on each frame.
 */
const ARC_SEGMENTS = 48;

/** One leg with its ISO instants parsed once, at payload ingest, and its lane already stamped. */
export type PlacedLeg = {
  city: string;
  from: GeoPoint;
  to: GeoPoint;
  takeoffAt: Date;
  landingAt: Date;
  /** The flight as a reader writes it, `""` when the payload carries no code and number. */
  number: string;
  fromCode: string;
  toCode: string;
  /**
   * The bow this leg is drawn with, in radians, signed. Stamped per route rather than per leg: two
   * flights a day on one city pair fly one line, and near-parallel routes are fanned apart.
   */
  laneRadians: number;
};

/**
 * One route as the route source carries it. `city` is the key the hover lights and gates on: the
 * same string `planeFeatures` writes, i.e. the city the leg is arriving into. `route` is the key
 * the source's own signature is built from, and `lane` is the bow the line was drawn with, which
 * has to ride the signature too: a route whose lane moved is a route whose geometry moved.
 */
export type LegFeature = {
  type: "Feature";
  geometry: { type: "LineString"; coordinates: Array<[number, number]> };
  properties: { city: string; route: string; lane: number };
};

/** One airport as a key: four decimals, the precision the placed set's own signature uses. */
export const pointKey = (point: GeoPoint): string =>
  `${point.lng.toFixed(4)},${point.lat.toFixed(4)}`;

/**
 * The route a leg flies, as a key: two flights a day on one city pair are one line.
 *
 * Direction-insensitive on purpose. A city pair is one route whichever way it is flown, so drawing
 * a departure beside its own arrival's line would be a second line for no information — and the
 * lane below is a per-route decision, which needs one key per pair to agree with this one.
 */
export const routeKey = (leg: PlacedLeg): string =>
  `${leg.city}|${[pointKey(leg.from), pointKey(leg.to)].sort().join("|")}`;

/**
 * One leg's own name, stable across ticks: its route and the instant it left.
 *
 * Two flights a day share a route, so the route alone cannot name a glyph — and both the declutter
 * and the pointer's hit test have to tell them apart.
 */
export const legKey = (leg: PlacedLeg): string => `${routeKey(leg)}|${leg.takeoffAt.toISOString()}`;

/**
 * Give each of a city's routes a lane of its own, so the bundle into one airport reads as a fan
 * rather than as one thick line.
 *
 * The ranking is over the city's whole payload and never over the set in the air right now: a lane
 * must not jump sideways on the tick another flight lands. Ranks alternate either side of the true
 * route and widen as they go — ±5 %, ±10 %, then ±15 % for every rank past that — which is a fan
 * five near-parallel routes can resolve.
 *
 * The sign travels with the geometry, not with the lane. `bowNormal` flips when a leg is flown the
 * other way, so the lift's sign is flipped with it, and one city pair keeps one bow whichever way
 * its flights are going.
 */
export const stampLanes = (legs: readonly PlacedLeg[]): void => {
  const byRoute = new Map<string, PlacedLeg[]>();
  for (const leg of legs) {
    const key = routeKey(leg);
    const group = byRoute.get(key);
    if (group) group.push(leg);
    else byRoute.set(key, [leg]);
  }

  [...byRoute.keys()].sort().forEach((key, rank) => {
    const group = byRoute.get(key) ?? [];
    const side = rank % 2 === 0 ? 1 : -1;
    const level = Math.min(Math.floor(rank / 2) + 1, LANE_LEVEL_MAX);
    for (const leg of group) {
      const span = distanceKm(leg.from, leg.to) / EARTH_RADIUS_KM;
      const lane = side * level * LANE_FRACTION * span;
      leg.laneRadians = pointKey(leg.from) <= pointKey(leg.to) ? lane : -lane;
    }
  });
};

/**
 * Every route with an aircraft on it right now, each drawn once.
 *
 * The clock rule is `planeFeatures`' own, so a route appears exactly when its aircraft does, and
 * the geometry is `legPath`'s — the same bow `interpolateLeg` places the aircraft on, sampled with
 * the same lane — so the glyph and the line under it cannot drift apart.
 *
 * One feature per route rather than per aircraft, because the lane belongs to the route: whichever
 * of its legs arrives first carries the same value every other one does.
 */
export const legFeatures = (legs: readonly PlacedLeg[], now: Date): LegFeature[] => {
  const features: LegFeature[] = [];
  const drawn = new Set<string>();
  for (const leg of legs) {
    if (legPhaseAt(leg, null, now) !== "in_progress") continue;
    const route = routeKey(leg);
    if (drawn.has(route)) continue;
    drawn.add(route);
    features.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: legPath(leg.from, leg.to, ARC_SEGMENTS, leg.laneRadians).map((point) => [
          point.lng,
          point.lat,
        ]),
      },
      properties: { city: leg.city, route, lane: leg.laneRadians },
    });
  }
  return features;
};

/**
 * The accent at a given alpha, for the one paint property that cannot carry a token: a route's own
 * gradient. Anything unparseable is returned untouched at both stops, so a palette change this
 * helper does not understand loses the fade and never the route.
 */
const withAlpha = (color: string, alpha: number): string => {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (hex) {
    const digits = hex[1] ?? "";
    const full =
      digits.length === 3 ? [...digits].map((digit) => `${digit}${digit}`).join("") : digits;
    const value = Number.parseInt(full, 16);
    return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
  }
  const functional = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(color);
  if (functional) return `rgba(${functional[1]}, ${functional[2]}, ${functional[3]}, ${alpha})`;
  return color;
};

/**
 * A route's own ink: faint where the leg left and full where it lands, so a bundle of routes
 * hanging off one city reads as traffic arriving rather than as a mesh with no direction.
 */
export const legGradient = (accent: string): MaplibreGl.ExpressionSpecification => [
  "interpolate",
  ["linear"],
  ["line-progress"],
  0,
  withAlpha(accent, 0.25),
  0.75,
  withAlpha(accent, 1),
];
